import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { withRouter } from 'react-router';
import PropTypes from 'prop-types';
import { get, isEmpty, map } from 'lodash';
import { formValueSelector, submit } from 'redux-form';
import { connect } from 'react-redux';
import { injectIntl, intlShape } from 'react-intl';
import Cookie from 'react-cookie';
import moment from 'moment/moment';
import Transactable from '../../shared-components/flow-transactable/transactable-main';
import SharedIntlProviderHOC from '../../shared-components/hocs/sharedIntlProviderHOC';
import { PageSpinner } from '../../shared-components/decorators/spinner';
import { InquiryMain } from '../../app-bookings-inquiry/InquiryMain';
import { INQUIRY_FORM } from '../../app-bookings-inquiry/InquiryForm/constants';
import { fetchListingInfo } from '../actions/listing';
import { Spaces } from '../../../../api/api-spaces';
import { useStatsigContext } from '../../elements-statsig-provider/utils';

const InquiryContainer = (props) => {
  const {
    params,
    dispatch,
    auth,
    profile,
    listing
  } = props;

  const { accessToken } = auth;

  const listingId = get(params, 'listingID', '');
  const [faqData, setFaqData] = useState({});

  // Load listing on page load
  useEffect(() => {
    dispatch(fetchListingInfo(listingId, (response) => {
      if (response.error && 'ACTIVATED' !== response.status) {
        location.href = `/pages/listings/${listingId}`;
      }
    }));
  }, [listingId]);

  useEffect(()=>{
    if (listing && listing.data && 'Event' === listing.data.use_type) {
      Spaces.getFaq(listing.data.parentSpaceId, 'event', accessToken, (res)=>{
        setFaqData(res.data);
      });
    }
  }, [listing]);

  // "Send message" button click handler
  const handleSubmitForm = useCallback(() => {
    analyticsIntegrations.formSubmit('msg host form');
    if (profile && accessToken) {
      ReactDOM.render(
        <SharedIntlProviderHOC>
          <Transactable
            user={profile}
            intendedAction={() => {
              dispatch(submit(INQUIRY_FORM));
            }}
          />
        </SharedIntlProviderHOC>,
        document.getElementById('modal-container')
      );
    }
  }, [profile.SSOid]);

  const { loading: statsigLoading } = useStatsigContext();

  return !listing.hasFetched || !profile?.hasFetched || statsigLoading ? (
    <PageSpinner additionalStyle='gray' />
  ) : (
    <InquiryMain
      {...props}
      handleSubmitForm={handleSubmitForm}
      listing={listing?.data}
      faqData={faqData}
    />
  );
};

const selector = formValueSelector(INQUIRY_FORM);

const mapStateToProps = (state) => {
  const { auth, profile, listing } = state;

  // Form values
  const schedule = selector(state, 'schedule');
  const scheduleInput = selector(state, 'scheduleInput');
  const numberOfGuests = selector(state, 'number_of_guests');
  const activity = selector(state, 'tags.activity');
  const alcoholConsumed = selector(state, 'alcohol_consumed');
  const message = selector(state, 'message');
  const organization = selector(state, 'organization');
  const profession = selector(state, 'profession');
  const flexibleHoursSelected = selector(state, 'flexible_hours_selected');

  // Initial values based on cookies, profile, listing details
  const profileOrganization = get(profile, 'data.organization', '');
  const profileProfession = get(profile, 'data.profession', '');
  const initialScheduleArray = Cookie.load('booking_schedule') || [{}];
  const initialSchedule = initialScheduleArray;
  const initialScheduleInput = !isEmpty(initialScheduleArray) ? map(initialScheduleArray, (obj) => {
    const { m, d, y } = get(obj, 'start.date', {});
    const dateString = `${m}-${d}-${y}`;
    return isEmpty(obj) ? {} : { date: moment(dateString, 'MM-DD-YYYY') };
  }) : '';
  const initialActivity = Cookie.load('saved-activity');
  const storedTier = Cookie.load('booking_attendee_tier');
  const initialNumberOfGuests = listing.data?.attendee_pricing?.tiers?.length > 1
    ? (storedTier && storedTier[listing.data?._id]?.min) ?? listing.data.attendee_pricing.tiers[0]?.min
    : undefined;

  return {
    auth,
    profile,
    listing,
    initialValues: {
      scheduleInput: initialScheduleInput,
      schedule: initialSchedule,
      number_of_guests: initialNumberOfGuests,
      tags: {
        activity: initialActivity
      },
      alcohol_consumed: '',
      message: '',
      organization: profileOrganization,
      profession: profileProfession,
      flexible_hours_selected: false
    },
    schedule: schedule || initialSchedule,
    scheduleInput,
    numberOfGuests,
    activity,
    alcoholConsumed,
    message,
    organization,
    profession,
    flexibleHoursSelected
  };
};

InquiryContainer.propTypes = {
  params: PropTypes.object,
  dispatch: PropTypes.func,
  auth: PropTypes.object,
  listing: PropTypes.object,
  profile: PropTypes.object,
  intl: intlShape
};

export default injectIntl(
  connect(mapStateToProps)(withRouter(InquiryContainer))
);
